/**
 * @file Warns when an MFE still scopes its CSS to the host container id. Delete this file and its
 * call in `MicroFrontend.tsx` once no deployed MFE does.
 */
import { DEFAULT_MFE_CONTAINER_ID } from './MicroFrontend.constants';

/** The id used as a selector anywhere in a rule: `#id .x`, `div#id` and `:is(#id)` alike. */
const HOST_CONTAINER_SELECTOR = new RegExp(`#${DEFAULT_MFE_CONTAINER_ID}(?![\\w-])`);

/**
 * True when a stylesheet still scopes to the host container id instead of
 * `[data-mfe-scope="<microFrontendWindowName>"]`. Such CSS cannot style the MFE's overlays, which
 * portal to an anchor on `body`, and restyles any other MFE sharing the id.
 */
export const isLegacyMfeStyle = (cssText: string): boolean => HOST_CONTAINER_SELECTOR.test(cssText);

/** Each MFE whose entry script is still loading, with the MFEs that loaded alongside it. */
const loading = new Map<string, Set<string>>();

/**
 * Non-production guard: collects the `<style>` elements added while an MFE's entry module
 * evaluates (a module script fires `load` after evaluation) and warns once if any still scopes to
 * the host container id. A style cannot be traced to the script that added it, so the warning
 * names any MFE that loaded at the same time.
 */
export const warnOnLegacyMfeStyle = (
  script: HTMLScriptElement,
  microFrontendWindowName: string,
  warn: (message: string) => void
): void => {
  const injected = new Set<HTMLStyleElement>();
  const loadedAlongside = new Set(
    [...loading.keys()].filter((name) => name !== microFrontendWindowName)
  );
  for (const others of loading.values()) others.add(microFrontendWindowName);
  loading.set(microFrontendWindowName, loadedAlongside);

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node instanceof HTMLStyleElement) injected.add(node);
      }
    }
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });

  // Aborting drops both listeners, so a script left in the DOM does not keep the styles alive.
  const listeners = new AbortController();
  const stop = () => {
    listeners.abort();
    observer.disconnect();
    loading.delete(microFrontendWindowName);
  };
  script.addEventListener('error', stop, { signal: listeners.signal });
  script.addEventListener(
    'load',
    () => {
      stop();
      // An earlier load of the same MFE may have added the template's tagged style already.
      const tagged = document.getElementById(`${microFrontendWindowName}-mfe-styles`);
      if (tagged instanceof HTMLStyleElement) injected.add(tagged);
      const offending = [...injected].some((style) => isLegacyMfeStyle(style.textContent ?? ''));
      injected.clear();
      if (!offending) return;
      const alongside = loadedAlongside.size
        ? ` (or ${[...loadedAlongside].join(', ')}, which loaded at the same time)`
        : '';
      warn(
        `Micro Frontend ${microFrontendWindowName}${alongside} still scopes its CSS to #${DEFAULT_MFE_CONTAINER_ID}. ` +
          `The container id is a mount contract only. Scope to [data-mfe-scope="${microFrontendWindowName}"] as ui-library requires.`
      );
    },
    { signal: listeners.signal }
  );
};
